'use client';

import dynamic from 'next/dynamic';
import type { Locale } from '@/lib/i18n/config';

// Skeleton placeholder — reserves space so the tool mount doesn't cause CLS.
// Height is an approximation; tweak if your tools render taller on first paint.
function ToolSkeleton() {
  return (
    <div
      className="w-full rounded-2xl bg-bg2 border border-borderDark animate-pulse"
      style={{ minHeight: '360px' }}
      aria-hidden="true"
    />
  );
}

// Tool component imports with ssr: false to prevent CSS preloading warnings.
// Every dynamic import gets the same skeleton so the page reserves space
// before the JS bundle loads, eliminating Cumulative Layout Shift.
const ImageConverter    = dynamic(() => import('@/components/tools/ImageConverter'),    { ssr: false, loading: () => <ToolSkeleton /> });
const ImageCompressor   = dynamic(() => import('@/components/tools/ImageCompressor'),   { ssr: false, loading: () => <ToolSkeleton /> });
const ImageResizer      = dynamic(() => import('@/components/tools/ImageResizer'),      { ssr: false, loading: () => <ToolSkeleton /> });
const ImageCropper      = dynamic(() => import('@/components/tools/ImageCropper'),      { ssr: false, loading: () => <ToolSkeleton /> });
const BackgroundRemover = dynamic(() => import('@/components/tools/BackgroundRemover'), { ssr: false, loading: () => <ToolSkeleton /> });
const ImageUpscaler     = dynamic(() => import('@/components/tools/ImageUpscaler'),     { ssr: false, loading: () => <ToolSkeleton /> });
const ImageRotator      = dynamic(() => import('@/components/tools/ImageRotator'),      { ssr: false, loading: () => <ToolSkeleton /> });
const JsonFormatter     = dynamic(() => import('@/components/tools/JsonFormatter'),     { ssr: false, loading: () => <ToolSkeleton /> });
const MetaTagGenerator  = dynamic(() => import('@/components/tools/MetaTagGenerator'), { ssr: false, loading: () => <ToolSkeleton /> });
const WordCounter       = dynamic(() => import('@/components/tools/WordCounter'),       { ssr: false, loading: () => <ToolSkeleton /> });
const Base64Tool        = dynamic(() => import('@/components/tools/Base64Tool'),        { ssr: false, loading: () => <ToolSkeleton /> });
const PdfWordCounter    = dynamic(() => import('@/components/tools/PdfWordCounter'),   { ssr: false, loading: () => <ToolSkeleton /> });
const ImagesToPdf       = dynamic(() => import('@/components/tools/ImagesToPdf'),      { ssr: false, loading: () => <ToolSkeleton /> });
const PdfToImages       = dynamic(() => import('@/components/tools/PdfToImages'),      { ssr: false, loading: () => <ToolSkeleton /> });
const PdfRotator        = dynamic(() => import('@/components/tools/PdfRotator'),       { ssr: false, loading: () => <ToolSkeleton /> });
const VideoToGif        = dynamic(() => import('@/components/tools/VideoToGif'),        { ssr: false, loading: () => <ToolSkeleton /> });
const PdfCompressor     = dynamic(() => import('@/components/tools/PdfCompressor'),     { ssr: false, loading: () => <ToolSkeleton /> });
const PdfMerger         = dynamic(() => import('@/components/tools/PdfMerger'),         { ssr: false, loading: () => <ToolSkeleton /> });
const QrCodeGenerator   = dynamic(() => import('@/components/tools/QrCodeGenerator'),   { ssr: false, loading: () => <ToolSkeleton /> });
const PdfSplitter       = dynamic(() => import('@/components/tools/PdfSplitter'),       { ssr: false, loading: () => <ToolSkeleton /> });
const VideoCompressor   = dynamic(() => import('@/components/tools/VideoCompressor'),   { ssr: false, loading: () => <ToolSkeleton /> });

// Keys MUST match the tool's `slug` field in data/tools.json.
export const tools = {
  'image-converter':        ImageConverter,
  'image-compressor':       ImageCompressor,
  'image-resizer':          ImageResizer,
  'image-cropper':          ImageCropper,
  'background-remover':     BackgroundRemover,
  'image-upscaler':         ImageUpscaler,
  'image-rotator':          ImageRotator,
  'json-formatter':         JsonFormatter,
  'meta-tag-generator':     MetaTagGenerator,
  'word-counter':           WordCounter,
  'base64-encoder-decoder': Base64Tool,
  'pdf-word-counter':       PdfWordCounter,
  'images-to-pdf':          ImagesToPdf,
  'pdf-to-images':          PdfToImages,
  'pdf-rotator':            PdfRotator,
  'video-to-gif':           VideoToGif,
  'pdf-compressor':         PdfCompressor,
  'pdf-merger':             PdfMerger,
  'qr-code-generator':      QrCodeGenerator,
  'pdf-splitter':           PdfSplitter,
  'video-compressor':       VideoCompressor,
  // Also not its own component — the circle-crop shape lock has always
  // lived inside ImageCropper.tsx itself (see isCircleCrop/CIRCLE_CROP_SLUG
  // there). This just gives it a real tools.json entry/main-tool listing
  // instead of being a CROP_SLUGS-derived sub-page of image-cropper.
  'circle-cropper':         ImageCropper,
} as const;

export type ToolName = keyof typeof tools;

interface ToolRendererProps {
  toolName: ToolName;
  locale?: Locale;
  initialConversionSlug?: string;
  initialFormatSlug?: string;
  initialCompressionSlug?: string;
  initialCropSlug?: string;
}

export function ToolRenderer({ toolName, locale = 'en', initialConversionSlug, initialFormatSlug, initialCompressionSlug, initialCropSlug }: ToolRendererProps) {
  const Tool = tools[toolName];

  if (!Tool) {
    return null;
  }

  // Special handling for ImageConverter which needs the conversion slug
  if (toolName === 'image-converter' && initialConversionSlug) {
    return <Tool initialConversionSlug={initialConversionSlug} locale={locale} />;
  }

  // Special handling for ImageCompressor which needs the compression slug
  if (toolName === 'image-compressor' && initialCompressionSlug) {
    return <Tool initialCompressionSlug={initialCompressionSlug} locale={locale} />;
  }

  // Special handling for ImageCropper which needs the crop slug
  if (toolName === 'image-cropper' && initialCropSlug) {
    return <Tool initialCropSlug={initialCropSlug} locale={locale} />;
  }

  // circle-cropper is a real tools.json entry, not a member of CROP_SLUGS
  // (removing it from that list avoids double-counting it in
  // getAllPublicPaths/sitemap/llms.txt, which iterate CROP_SLUGS separately
  // from tools.json). Hardcode the lock here instead of relying on the
  // page-level isCropSlug() check — this is the same pattern heic-to-jpg
  // used before it was migrated into IMAGE_CONVERSION_SLUGS (see lib/data.ts).
  if (toolName === 'circle-cropper') {
    return <Tool initialCropSlug="circle-cropper" locale={locale} />;
  }

  // Special handling for PdfToImages which needs the format slug
  if (toolName === 'pdf-to-images' && initialFormatSlug) {
    return <Tool initialFormatSlug={initialFormatSlug} locale={locale} />;
  }

  // Special handling for ImagesToPdf which needs the format slug
  if (toolName === 'images-to-pdf' && initialFormatSlug) {
    return <Tool initialFormatSlug={initialFormatSlug} locale={locale} />;
  }

  return <Tool locale={locale} />;
}
